import type { Row, SortingState } from "@tanstack/react-table";
import { getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import clsx from "clsx";
import type { FunctionComponent, MouseEvent } from "react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Card from "~/components/Card/Card";
import SearchInput from "~/components/formElements/SearchInput/SearchInput";
import Table from "~/components/Table/Table";
import { INVOICE_DETAILS_PATH_WITH_ORIGIN } from "~/constants/appRoutes.ts";
import { PROJECTS_INVOICES_TABLE_PAGE_STATE_KEY } from "~/constants/pageStateStorageKeys.ts";
import { useAuth } from "~/contexts/AuthContext";
import usePageStateStorage from "~/hooks/usePageStateStorage";
import {
	validInvoiceDetailsNavigatedFromOrigins,
} from "~/modules/billing/components/InvoiceDetailsView/invoiceDetailsUtils.ts";
import type { ProjectPhase } from "~/modules/project/api/projectPhase/projectPhaseTypes.ts";
import PhaseFilter from "~/modules/project/components/ProjectDetailsView/components/PhaseFilter";
import {
	projectsInvoicesTableColumnDefs,
} from "~/modules/project/components/ProjectDetailsView/components/ProjectBillingSectionTabContent/components/ProjectInvoicesTabContent/components/ProjectsInvoicesTable/projectsInvoicesTableColumnDefs.tsx";
import type {
	ProjectsInvoicesTableData,
} from "~/modules/project/components/ProjectDetailsView/components/ProjectBillingSectionTabContent/components/ProjectInvoicesTabContent/components/ProjectsInvoicesTable/projectsInvoicesTableTypes.ts";
import { PermissionNames } from "~/types/entityNames.ts";
import type { ColumnFilterType } from "~/types/tanstackTableTypes.ts";
import {
	createColumnFilterInputHandler,
	getColumnFilterValueForKey,
	sanitiseColumnFilters,
} from "~/utils/tanStackTableUtils.ts";

type Props = {
	isLoading: boolean;
	projectId: string;
	projectPhases: ProjectPhase[];
	tableData: ProjectsInvoicesTableData[];
};
const defaultPageState = {
	globalFilter: "",
	sorting: [{ id: "deliveryStartDate", desc: true }],
	columnFilters: [
		{ id: "phaseIds", value: [] },
	],
};

const ProjectsInvoicesTable: FunctionComponent<Props> = ({ isLoading, projectId, projectPhases, tableData }) => {
	const { hasAnyPermission } = useAuth();

	const userCanManageInvoices = hasAnyPermission(PermissionNames.CanManageInvoices);
	const pageKey = PROJECTS_INVOICES_TABLE_PAGE_STATE_KEY(projectId);
	const { pageState, setPageState } = usePageStateStorage({
		pageKey,
		defaultState: defaultPageState,
	});
	const [columnFilters, setColumnFilters] = useState<ColumnFilterType[]>(
		sanitiseColumnFilters(defaultPageState.columnFilters, pageState.columnFilters));
	const [globalFilter, setGlobalFilter] = useState<string>(pageState.globalFilter);
	const [sorting, setSorting] = useState<SortingState>(pageState.sorting);

	const navigate = useNavigate();

	const handleColumnFilterInputChange = createColumnFilterInputHandler(columnFilters, setColumnFilters);

	useEffect(() => {
		setPageState({ sorting, globalFilter, columnFilters });
	}, [globalFilter, setPageState, sorting, columnFilters]);

	const handleRowClick = useCallback(
		(row: Row<ProjectsInvoicesTableData>, event: MouseEvent) => {
			const path = INVOICE_DETAILS_PATH_WITH_ORIGIN(row.original.id, validInvoiceDetailsNavigatedFromOrigins.PROJECT_INVOICES);

			if (event.ctrlKey || event.metaKey) {
				window.open(path, "_blank");
			} else {
				navigate(path);
			}
		},
		[navigate],
	);

	const table = useReactTable({
		data: tableData,
		columns: projectsInvoicesTableColumnDefs,
		onSortingChange: setSorting,
		state: {
			columnFilters,
			globalFilter,
			sorting,
		},
		meta: {
			onRowClick: userCanManageInvoices ? handleRowClick : undefined,
		},
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		sortDescFirst: true,
	});


	return <div className={clsx("auto-grid-2-rows size-full overflow-hidden")}>
		<div className="mt-4">
			<Card>
				<div className="flex items-center gap-x-4">
					<div className="w-1/4">
						<SearchInput value={globalFilter}
									 autoFocus={true}
									 onChange={setGlobalFilter} />
					</div>
					<PhaseFilter projectPhases={projectPhases}
								 filterIds={getColumnFilterValueForKey("phaseIds", columnFilters)}
								 onChange={(value) => handleColumnFilterInputChange("phaseIds", value)} />
				</div>
			</Card>
		</div>
		<div className="size-full overflow-hidden pb-10 pt-4">
			<Table table={table}
				   isLoading={isLoading}
				   fixedWidth={true}
				   pagination={false} />
		</div>
	</div>;

};

export default ProjectsInvoicesTable;